import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import { useFormularios, usePendientes, type Formulario, type Pendiente } from './api';
import { DisenadorDeFormulario } from './DisenadorDeFormulario';
import { Invitar, Invitaciones } from './Invitaciones';
import { RevisarRespuesta } from './RevisarRespuesta';

/**
 * Recolección de Datos — `#gestion-conocimiento/recoleccion`.
 *
 * Formularios para delegar el registro: se invita por correo, la persona los
 * diligencia desde fuera y Bienestar revisa lo que llega.
 *
 * ## Lo que cambia
 *
 * - **La tarjeta se abre con el teclado.** Era un `<div onclick>` con dos
 *   botones dentro, y el propio vanilla explicaba que no podía convertirse en
 *   `<button>` por eso. Aquí el nombre es el enlace y los botones van fuera de
 *   él, que es la solución que aquel comentario pedía.
 * - **Los nombres ya no viajan dentro de un `onclick`** con las comillas
 *   simples quitadas. Un nombre con comillas dobles rompía el manejador — y lo
 *   escribe quien diligencia, que puede ser alguien de fuera.
 */
export function Recoleccion() {
  const consulta = useFormularios(true);
  const pendientes = usePendientes(true);

  const [editando, setEditando] = useState<number | null | undefined>(undefined);
  const [invitandoA, setInvitandoA] = useState<Formulario | null>(null);
  const [viendoInvitaciones, setViendoInvitaciones] = useState<Formulario | null>(null);
  const [revisando, setRevisando] = useState<number | null>(null);

  const formularios = consulta.data ?? [];

  return (
    <>
      {/* La bandeja va arriba porque es lo único con fecha de caducidad: el
          resto de la pantalla es configuración. */}
      {!!pendientes.data?.length && (
        <BandejaDeRevisiones filas={pendientes.data} onRevisar={setRevisando} />
      )}

      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-input-cursor-text" aria-hidden="true" /> Recolección de Datos
          </h1>
          <p className="mt-1 text-3 text-muted">
            Formularios para delegar el registro, con aprobación y destino configurable.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href="#recursos-privados"
            className="inline-flex items-center gap-1 self-center text-3 font-semibold text-brand-ink hover:underline focus-visible:focus-anillo"
          >
            <i className="bi bi-hdd-network" aria-hidden="true" /> Bases de datos
          </a>
          <Button size="sm" onClick={() => setEditando(null)}>
            <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nuevo formulario
          </Button>
        </div>
      </header>

      {consulta.isPending ? (
        <SkeletonTarjetas n={4} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="los formularios de recolección"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !formularios.length ? (
        <EstadoVacio
          icono="bi-input-cursor-text"
          titulo="Sin formularios"
          descripcion="Crea el primero: registro de nuevos colaboradores o actualización de datos."
          accion={{ texto: 'Nuevo formulario', onClick: () => setEditando(null) }}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {formularios.map((f) => (
            <li key={f.id} className="flex">
              <TarjetaDeFormulario
                formulario={f}
                onEditar={() => setEditando(f.id)}
                onInvitar={() => setInvitandoA(f)}
                onVerInvitaciones={() => setViendoInvitaciones(f)}
              />
            </li>
          ))}
        </ul>
      )}

      {editando !== undefined && (
        <DisenadorDeFormulario id={editando} onCerrar={() => setEditando(undefined)} />
      )}
      {invitandoA && (
        <Invitar formulario={invitandoA} onCerrar={() => setInvitandoA(null)} />
      )}
      {viendoInvitaciones && (
        <Invitaciones
          formulario={viendoInvitaciones}
          onRevisar={(id) => { setViendoInvitaciones(null); setRevisando(id); }}
          onCerrar={() => setViendoInvitaciones(null)}
        />
      )}
      {revisando != null && (
        <RevisarRespuesta id={revisando} onCerrar={() => setRevisando(null)} />
      )}
    </>
  );
}

/* ── La bandeja ────────────────────────────────────────────────────────── */

const DIAS_VIEJA = 7;

function diasDesde(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 86400000) : null;
}

function hace(dias: number | null): string {
  if (dias === null) return '';
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
}

/**
 * Las respuestas que esperan revisión.
 *
 * `/data-forms/reviews/pending` existía desde la Fase C y no lo llamaba nadie:
 * para encontrar una respuesta había que entrar formulario por formulario. Con
 * el correo de aviso opcional —`notify_emails` puede ir vacío— una respuesta
 * podía quedarse esperando sin que nadie lo supiera.
 */
function BandejaDeRevisiones({
  filas, onRevisar,
}: {
  filas: Pendiente[];
  onRevisar: (id: number) => void;
}) {
  const viejas = filas.filter((r) => (diasDesde(r.submitted_at) ?? 0) >= DIAS_VIEJA).length;

  return (
    <section className="mb-5 rounded-md border border-warn/40 bg-warn/12">
      <h2 className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-3 font-bold text-warn-ink">
        <i className="bi bi-inbox-fill" aria-hidden="true" />
        {filas.length} {filas.length === 1 ? 'respuesta esperando revisión' : 'respuestas esperando revisión'}
        {viejas > 0 && (
          <span className="text-2 font-semibold">
            <i className="bi bi-clock-history" aria-hidden="true" />{' '}
            {viejas} {viejas === 1 ? 'lleva' : 'llevan'} más de {DIAS_VIEJA} días
          </span>
        )}
      </h2>
      <ul className="border-t border-warn/30">
        {filas.map((r) => {
          const dias = diasDesde(r.submitted_at);
          const vieja = (dias ?? 0) >= DIAS_VIEJA;
          return (
            <li
              key={r.id}
              className="flex flex-wrap items-center gap-2 border-b border-warn/20 px-4 py-2 last:border-0"
            >
              <strong className="text-3 text-ink">{r.who || '—'}</strong>
              {r.is_new_person && <Badge tone="info">Nuevo/externo</Badge>}
              <span className="text-2 text-muted">{r.form_name}</span>
              <span className={vieja ? 'text-2 font-semibold text-danger-ink' : 'text-2 text-muted'}>
                {hace(dias)}
              </span>
              <Button size="sm" className="ml-auto" onClick={() => onRevisar(r.id)}>
                <i className="bi bi-eye" aria-hidden="true" /> Revisar
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ── Una tarjeta ───────────────────────────────────────────────────────── */

function TarjetaDeFormulario({
  formulario: f, onEditar, onInvitar, onVerInvitaciones,
}: {
  formulario: Formulario;
  onEditar: () => void;
  onInvitar: () => void;
  onVerInvitaciones: () => void;
}) {
  const total = f.total_responses || 0;
  const respondidas = total - (f.responses?.pendiente ?? 0);
  const pct = total ? Math.round((respondidas * 100) / total) : 0;

  return (
    <Card interactiva className="flex w-full flex-col overflow-hidden">
      {/* El color lo elige quien crea el formulario: es dato, no tema. */}
      <span
        className="flex h-20 items-center justify-center text-8 text-on-brand"
        style={{ background: f.color }}
        aria-hidden="true"
      >
        <Icono nombre={f.icon} respaldo="bi-input-cursor-text" />
      </span>

      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <h3 className="text-3 font-bold text-brand-ink">
          <button
            type="button"
            className="text-left hover:underline focus-visible:focus-anillo"
            onClick={onEditar}
          >
            {f.name}
          </button>
        </h3>
        <p className="text-2 text-muted">
          <i className="bi bi-box-arrow-in-down" aria-hidden="true" />{' '}
          {f.target_resource_name || f.target_label}
        </p>
        <p className="flex flex-wrap items-center gap-2">
          <Badge tone={f.approval_mode === 'auto' ? 'ok' : 'warn'}>
            {f.approval_mode === 'auto' ? 'Auto-aprobación' : 'Aprobación manual'}
          </Badge>
          <span className="ml-auto text-1 text-muted">
            {f.field_count} {f.field_count === 1 ? 'campo' : 'campos'}
          </span>
        </p>

        {total > 0 && (
          <div className="mt-0.5">
            <p className="flex justify-between text-1 text-muted">
              <span>{respondidas} de {total} {respondidas === 1 ? 'respondida' : 'respondidas'}</span>
              <strong className="tabular-nums">{pct}%</strong>
            </p>
            <BarraDeProgreso pct={pct} etiqueta={`Respuestas de ${f.name}`} />
          </div>
        )}

        <div className="mt-auto flex gap-2 pt-2">
          <Button size="sm" className="flex-1" onClick={onInvitar}>
            <i className="bi bi-send" aria-hidden="true" /> Invitar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconOnly
            aria-label={`Ver las invitaciones de ${f.name}`}
            onClick={onVerInvitaciones}
          >
            <i className="bi bi-list-check" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
